import React, { useState } from 'react';
import { Mic, MicOff, Phone } from 'lucide-react';
import { CompanyConfig } from '@/constants/cx-companies';
import { CXTranscript } from './CXTranscript';

interface TranscriptEntry {
  source: 'ai' | 'user';
  message: string;
  timestamp: Date;
}

interface CXCallProps {
  config: CompanyConfig;
  onEndCall: (duration: number) => void;
  transcript: TranscriptEntry[];
  isSpeaking: boolean;
  isConnected: boolean;
  callDuration: number;
}

export const CXCall: React.FC<CXCallProps> = ({
  config,
  onEndCall,
  transcript,
  isSpeaking,
  isConnected,
  callDuration,
}) => {
  const [isMuted, setIsMuted] = useState(false);

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
    // TODO: Implement actual mute control via conversation API
  };

  const handleEndCall = () => {
    onEndCall(callDuration);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-4">
              <img src={config.logo} alt={config.name} className="h-6 md:h-8" />
              <div className="flex items-center gap-1">
                <span className="text-gray-400 text-xs md:text-sm">Powered by</span>
                <img src={config.hupoLogo} alt="Hupo" className="h-5 md:h-6 mt-0.5" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col lg:flex-row h-[calc(100vh-73px)] bg-gray-100">
        <div className="flex flex-col lg:flex-row w-full max-w-7xl mx-auto p-4 lg:p-8 gap-4 lg:gap-8">
          {/* Left side - Call interface */}
          <div className="flex-1 bg-white rounded-2xl shadow-lg flex items-center justify-center p-4 lg:p-8 min-h-[400px] lg:min-h-0">
            <div className="flex flex-col items-center">
              {/* Avatar with pulse animation */}
              <div className="relative mb-4 lg:mb-6">
                <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full bg-gray-200 flex items-center justify-center">
                  <div
                    className="w-28 h-28 lg:w-36 lg:h-36 rounded-full flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${config.colors.accent}, ${config.colors.primary})`,
                      animation: isConnected ? 'pulse 2s infinite' : 'none',
                    }}
                  >
                    <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-full bg-gray-200" />
                  </div>
                </div>
              </div>

              {/* Status */}
              <p className="text-gray-500 mb-2 text-sm lg:text-base">
                {isConnected ? `On call with ${config.chat.agentName}` : 'Connecting...'}
              </p>

              {/* Timer */}
              <div
                className="text-2xl lg:text-3xl font-bold mb-4 lg:mb-6"
                style={{ color: config.colors.primaryDark }}
              >
                {formatDuration(callDuration)}
              </div>

              {/* Controls */}
              <div className="flex items-center gap-4">
                <button
                  onClick={handleMuteToggle}
                  className="p-3 lg:p-4 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                  disabled={!isConnected}
                >
                  {isMuted ? (
                    <MicOff className="w-5 h-5 lg:w-6 lg:h-6" />
                  ) : (
                    <Mic className="w-5 h-5 lg:w-6 lg:h-6" />
                  )}
                </button>

                <button
                  onClick={handleEndCall}
                  className="p-3 lg:p-4 rounded-full bg-red-500 hover:bg-red-600 transition-colors"
                >
                  <Phone className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Right side - Transcript */}
          <div className="flex-1 bg-white rounded-2xl shadow-lg overflow-hidden min-h-[300px] lg:min-h-0">
            <CXTranscript
              transcript={transcript}
              isSpeaking={isSpeaking}
              agentName={config.chat.agentName}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(196, 52, 124, 0.4);
          }
          70% {
            box-shadow: 0 0 0 20px rgba(196, 52, 124, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(196, 52, 124, 0);
          }
        }
      `}</style>
    </div>
  );
};